import ApiService from "./api-service";

const endpoint = "/users";
const wishlistEndpoint = "/wishlists";
const twoFactorAuthBaseEndpoint = "/auth/two-factor";
const passwordResetBaseEndpoint = "/password/reset";

interface ResetPasswordDataType { 
  password?: string; 
  resetCode?: string; 
  userId?: string;
  fromAccountSettings?: boolean; 
  resendTwoFactorAuthCode?: boolean; 
}

interface ResetPasswordWithTwoFactorAuthDataType { 
  password: string; 
  authCode: string; 
  userId?: string;
  endpoint?: string; 
}

export default class UserService {
  static async createUser(userData) {
    return await ApiService.post(endpoint, undefined, userData);
  }

  static async resendVerificationCode({ email }) {
    return await ApiService.post("/email/verification/resend", undefined, { email });
  }

  static async verifyEmail(verificationCode) {
    return await ApiService.get(
      `/email/verification/verify?verificationCode=${verificationCode}`
    );
  }

  static async login(loginCreds) {
    return await ApiService.post("/login", undefined, loginCreds);
  }

  static async loginWithTwoFactorAuth(authCode: string, endpoint?: string) { 
    endpoint = endpoint || "/login/two-factor-auth";

    return await ApiService.post(endpoint, null, { auth_code: authCode });
  }

  static async requestEnableTwoFactorAuth(authMethod: string) { 
    const endpoint = `${twoFactorAuthBaseEndpoint}/enable/request`;

    return await ApiService.post(endpoint, null, { auth_method: authMethod }); 
  }

  static async enableTwoFactorAuth(authCode: string) { 
    const endpoint = `${twoFactorAuthBaseEndpoint}/enable`;

    return await ApiService.post(endpoint, null, { auth_code: authCode }); 
  }

  static async requestDisableTwoFactorAuth() { 
    const endpoint = `${twoFactorAuthBaseEndpoint}/disable/request`;

    return await ApiService.post(endpoint, null, {});
  }

  static async disableTwoFactorAuth(authCode: string) { 
    const endpoint = `${twoFactorAuthBaseEndpoint}/disable`;

    return await ApiService.post(endpoint, null, { auth_code: authCode }); 
  }

  static async logout() {
    return await ApiService.get("/logout");
  }

  static async requestPasswordReset(email: string) { 
    const endpoint = `${passwordResetBaseEndpoint}/request`;

    return await ApiService.post(endpoint, null, { email });
  }

  static async resetPassword(data: ResetPasswordDataType) { 
    const endpoint = passwordResetBaseEndpoint;
    const { password, resetCode, userId, fromAccountSettings, resendTwoFactorAuthCode } = data;

    return await ApiService.put(endpoint, null, { 
      password, 
      reset_code                  : resetCode, 
      user_id                     : userId, 
      from_account_settings       : fromAccountSettings, 
      resend_two_factor_auth_code : resendTwoFactorAuthCode, 
    });
  }

  static async resetPasswordWithTwoFactorAuth(data: ResetPasswordWithTwoFactorAuthDataType) { 
    const { 
      userId, 
      password, 
      authCode, 
      endpoint = `${passwordResetBaseEndpoint}/two-factor-auth`,
    } = data;

    return await ApiService.put(endpoint, null, { password, user_id: userId, auth_code: authCode });
  }

  static async getUser(userId) {
    return await ApiService.get(`${endpoint}/:userId`, { userId });
  }

  static async getCurrentUser() {
    return await ApiService.get("/authenticate");
  }

  static async addItemToUserWishlist(userId: string, item) {
    return await ApiService.post(wishlistEndpoint, undefined, {
      user_id: userId,
      entity_id: item.id,
      entity_type: item.type,
    });
  }

  static async removeItemFromUserWishlist(userId: string, itemId) {
    return await ApiService.delete(wishlistEndpoint, undefined, {
      user_id: userId,
      entity_id: itemId
    });
  }

  static async getUserWishlist(userId: string, params?: { page?: number; limit?: number; }) {
    return await ApiService.get(`${wishlistEndpoint}/${userId}`, params);
  }

  static async getUserWishlistCount(userId: string) {
    return await ApiService.get(`${wishlistEndpoint}/${userId}/count`);
  }
}
